/**
 * gestures.js —— 手势判定
 *
 * 纯函数 + 一个防抖器，不依赖 three、不依赖 DOM，
 * 所以可以拿离线录制的 landmark 序列跑回归测试。
 *
 * 输入是 MediaPipe Hands 的 21 个关键点（归一化坐标 {x,y,z}）。
 * 判定逻辑：比较「指尖到腕」与「指根到腕」的距离比。
 * 用比值而不是绝对距离，是为了对手离摄像头远近不敏感。
 */

import { AIM, GESTURE } from './tuning.js';

const WRIST = 0;
const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
/** 各指的 [指根 MCP, 指尖 TIP] */
const FINGERS = {
  thumb: [2, 4],
  index: [5, 8],
  middle: [9, 12],
  ring: [13, 16],
  pinky: [17, 20]
};

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, (a.z || 0) - (b.z || 0));

/**
 * 设置抽屉打开或用户正在编辑表单时，摄像头可以继续识别，
 * 但不允许手势改变起卦状态。
 */
export function isGestureControlBlocked({ settingsOpen = false, activeElement = null } = {}) {
  const tagName = activeElement?.tagName?.toUpperCase?.() || '';
  return Boolean(settingsOpen || activeElement?.isContentEditable || EDITABLE_TAGS.has(tagName));
}

/** 每根手指的伸展比：指尖到腕 / 指根到腕 */
export function fingerRatios(landmarks) {
  const w = landmarks[WRIST];
  const out = {};
  for (const [name, [mcp, tip]] of Object.entries(FINGERS)) {
    const base = dist(landmarks[mcp], w) || 1e-6;
    out[name] = dist(landmarks[tip], w) / base;
  }
  return out;
}

/**
 * 判定手势。离线标定测试可直接传 fingerRatios() 的结果；实时路径仍传 21 点。
 *
 * @param {Array<object>|object|null} input 21 个 landmarks 或五指 ratios
 * @returns 'fist' | 'palm' | 'point' | null
 */
export function classify(input) {
  if (!input) return null;
  const four = ['index', 'middle', 'ring', 'pinky'];
  const r = Array.isArray(input)
    ? input.length >= 21 ? fingerRatios(input) : null
    : input;
  if (!r || !four.every((finger) => Number.isFinite(r[finger]))) return null;
  const extended = four.filter((f) => r[f] > GESTURE.extendedRatio);
  const curled = four.filter((f) => r[f] < GESTURE.curledRatio);

  if (curled.length >= 4) return 'fist';
  if (extended.length >= 4) return 'palm';
  if (r.index > GESTURE.extendedRatio && curled.length >= 2) return 'point';
  return null;
}

/** 掌心朝向：由腕、食指根、小指根构成的平面法线，取 roll 角。 */
export function palmOrientation(landmarks, { centerX = 0.5, centerY = 0.5 } = {}) {
  if (!landmarks || landmarks.length < 21) return { roll: 0, x: 0, y: 0 };
  const w = landmarks[WRIST];
  const idx = landmarks[5];
  const pinky = landmarks[17];
  const roll = Math.atan2(pinky.y - idx.y, pinky.x - idx.x);
  const cx = (w.x + idx.x + pinky.x) / 3;
  const cy = (w.y + idx.y + pinky.y) / 3;
  // MediaPipe 的 x 是镜像的，取负让画面跟手
  return { roll, x: -(cx - centerX) * 2, y: (cy - centerY) * 2, cx, cy };
}

/**
 * 把平滑后的掌心输入映射成 Three.js 相对转角。
 * 保持原交互方向：手向下移动产生正俯仰，手向上移动产生负俯仰。
 * 中性位置由实测 palmNeutralY 校准，不用反号或额外 bias 修正。
 */
export function palmRotationTarget(
  smoothed,
  { maxTiltX, maxTiltY, maxRollZ, pitchBias = 0 }
) {
  const clamp = (value) => Math.max(-1, Math.min(1, value));
  return {
    x: pitchBias + clamp(smoothed.x) * maxTiltX,
    y: clamp(smoothed.y) * maxTiltY,
    z: clamp(smoothed.z) * maxRollZ
  };
}

/**
 * 把食指指尖映射到相机 z=0 平面的世界坐标。
 * width / height 是该平面在当前透视相机里的可见尺寸；MediaPipe x
 * 为镜像坐标，因此水平方向取负，保证屏幕里的阵眼与手同向移动。
 */
export function fingertipAimPosition(landmarks, { width, height }) {
  const tip = landmarks?.[8];
  if (!tip || !Number.isFinite(tip.x) || !Number.isFinite(tip.y)) return null;
  const normalizedX = Math.max(-1, Math.min(1, -(tip.x - 0.5) * 2));
  const normalizedY = Math.max(-1, Math.min(1, -(tip.y - 0.5) * 2));
  return {
    x: normalizedX * width * AIM.followRangeX,
    y: normalizedY * height * AIM.followRangeY
  };
}

/**
 * 防抖：连续 stableFrames 帧一致才确认，且两次确认之间有冷却。
 * 只在手势**变化**时回调，避免握着拳不放被判成连续摇卦。
 */
export class GestureDebouncer {
  constructor(o = {}) {
    this.stable = o.stableFrames ?? GESTURE.stableFrames;
    this.cooldown = o.cooldown ?? GESTURE.cooldown;
    this.current = null;
    this._candidate = null;
    this._count = 0;
    this._since = Infinity;
  }

  /**
   * @param {string|null} raw  classify() 的结果
   * @param {number} dt        秒
   * @returns 手势发生变化时返回新手势，否则 null
   */
  feed(raw, dt) {
    this._since += dt;
    if (raw === this._candidate) {
      this._count += 1;
    } else {
      this._candidate = raw;
      this._count = 1;
    }
    if (this._count < this.stable) return null;
    if (raw === this.current) return null;
    if (this._since < this.cooldown) return null;
    this.current = raw;
    this._since = 0;
    return raw;
  }

  /** 界面交互期间丢弃已累积的手势，恢复后必须重新稳定识别。 */
  reset() {
    this.current = null;
    this._candidate = null;
    this._count = 0;
    this._since = Infinity;
  }
}

/** 指数平滑，用于掌心控向，避免画面抖 */
export class Smoother {
  constructor(alpha) {
    this.alpha = alpha;
    this.value = null;
  }
  push(v) {
    if (this.value === null) this.value = { ...v };
    else for (const k of Object.keys(v)) this.value[k] += (v[k] - this.value[k]) * this.alpha;
    return this.value;
  }
}
